import { useEffect, useState, useCallback } from 'react';
import { RootState, AppDispatch } from '../store/store';
import { MilestoneMap1, UserWithAccessAndRole } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { loadMilestoneMaps, updateMilestoneMap } from '../store/admin1Slice';
import { Dialog, DialogTitle, DialogDescription } from '../components_catalyst/dialog';
import { Button } from '../components_catalyst/button';
import { Input } from '../components_catalyst/input';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';
import { collection, getDocs } from 'firebase/firestore';

export function AdminMilestoneMapsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const milestoneMaps = useSelector((state: RootState) => state.admin1.milestoneMaps);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedMap, setSelectedMap] = useState<MilestoneMap1 | null>(null);

  useEffect(() => {
    dispatch(loadMilestoneMaps());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    await dispatch(loadMilestoneMaps());
  }, [dispatch]);

  const handleEdit = (map: MilestoneMap1) => {
    setSelectedMap(map);
    setIsEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedMap) return;
    try {
      await dispatch(
        updateMilestoneMap({
          uuid: selectedMap.uuid,
          updates: selectedMap,
        }),
      );
      setIsEditDialogOpen(false);
      setSelectedMap(null);
    } catch (error) {
      console.error('Error updating milestone map:', error);
    }
  };

  const handleAddControl = () => {
    setSelectedMap((prev) => {
      if (!prev) return null;
      const newUsersWithAccess = [...(prev.usersWithAccess || [])];
      newUsersWithAccess.push({
        uid: '',
        userName: '',
        role: 'viewer',
      } as UserWithAccessAndRole);
      return { ...prev, usersWithAccess: newUsersWithAccess };
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Milestone Maps</h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <SaveAndRefresh onRefresh={handleRefresh} />
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">UUID</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Short ID
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {milestoneMaps?.map((map) => (
                  <tr key={map.uuid}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button color="blue" onClick={() => handleEdit(map)}>
                        Edit
                      </Button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{map.uuid}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{map.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{map.shortId}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{map.owner?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
        <DialogTitle>Edit Milestone Map</DialogTitle>
        <DialogDescription>Update the milestone map details below.</DialogDescription>

        <div className="mt-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium">Basic Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <Input
                value={selectedMap?.title || ''}
                onChange={(e) =>
                  setSelectedMap((prev) => (prev ? { ...prev, title: e.target.value } : null))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Short ID</label>
              <Input
                value={selectedMap?.shortId || ''}
                onChange={(e) =>
                  setSelectedMap((prev) => (prev ? { ...prev, shortId: e.target.value } : null))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">UUID</label>
              <Input value={selectedMap?.uuid || ''} disabled />
            </div>
          </div>

          {/* Timing Settings */}
          <div className="space-y-4">
            <h3 className="font-medium">Timing Settings</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Interval</label>
              <select
                value={selectedMap?.interval || 'weeks'}
                onChange={(e) =>
                  setSelectedMap((prev) =>
                    prev
                      ? { ...prev, interval: e.target.value as MilestoneMap1['interval'] }
                      : null,
                  )
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="biweekly">Biweekly</option>
                <option value="months">Months</option>
                <option value="quarters">Quarters</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Interval in Days</label>
              <Input
                type="number"
                value={selectedMap?.intervalInDays || ''}
                onChange={(e) =>
                  setSelectedMap((prev) =>
                    prev ? { ...prev, intervalInDays: Number(e.target.value) } : null,
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date</label>
              <Input
                type="date"
                value={selectedMap?.startDate || ''}
                onChange={(e) =>
                  setSelectedMap((prev) => (prev ? { ...prev, startDate: e.target.value } : null))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">End Date</label>
              <Input
                type="date"
                value={selectedMap?.endDate || ''}
                onChange={(e) =>
                  setSelectedMap((prev) => (prev ? { ...prev, endDate: e.target.value } : null))
                }
              />
            </div>
          </div>

          {/* Owner Information */}
          <div className="space-y-4">
            <h3 className="font-medium">Owner Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Owner Name</label>
              <Input
                value={selectedMap?.owner?.name || ''}
                onChange={(e) =>
                  setSelectedMap((prev) =>
                    prev ? { ...prev, owner: { ...prev.owner, name: e.target.value } } : null,
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Owner UID</label>
              <Input
                value={selectedMap?.owner?.uid || ''}
                onChange={(e) =>
                  setSelectedMap((prev) =>
                    prev ? { ...prev, owner: { ...prev.owner, uid: e.target.value } } : null,
                  )
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Owner Type</label>
              <select
                value={selectedMap?.owner?.entityType || 'user'}
                onChange={(e) =>
                  setSelectedMap((prev) =>
                    prev
                      ? {
                          ...prev,
                          owner: { ...prev.owner, entityType: e.target.value as 'user' | 'org' },
                        }
                      : null,
                  )
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="user">User</option>
                <option value="org">Organization</option>
              </select>
            </div>
          </div>

          {/* Access Controls */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Access Controls</h3>
              <Button color="zinc" onClick={handleAddControl}>
                Add Access Control
              </Button>
            </div>
            {selectedMap?.usersWithAccess?.map(
              (userAccessRole: UserWithAccessAndRole, index: number) => (
                <div key={index} className="space-y-4 p-4 border rounded-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      value={userAccessRole.userName}
                      onChange={(e) => {
                        const newControls = [...(selectedMap.usersWithAccess || [])];
                        newControls[index] = { ...userAccessRole, userName: e.target.value };
                        setSelectedMap((prev) =>
                          prev ? { ...prev, usersWithAccess: newControls } : null,
                        );
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      value={userAccessRole.role}
                      onChange={(e) => {
                        const newControls = [...(selectedMap.usersWithAccess || [])];
                        newControls[index] = {
                          ...userAccessRole,
                          role: e.target.value as UserWithAccessAndRole['role'],
                        };
                        setSelectedMap((prev) =>
                          prev ? { ...prev, usersWithAccess: newControls } : null,
                        );
                      }}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="commenter">Commenter</option>
                      <option value="editor">Editor</option>
                    </select>
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Feature Flags */}
          <div className="space-y-4">
            <h3 className="font-medium">Feature Flags</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedMap?.featureFlags?.enableRelatives || false}
                  onChange={(e) =>
                    setSelectedMap((prev) =>
                      prev
                        ? {
                            ...prev,
                            featureFlags: {
                              ...prev.featureFlags,
                              enableRelatives: e.target.checked,
                            },
                          }
                        : null,
                    )
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Enable Relatives</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedMap?.featureFlags?.enablePlaylists || false}
                  onChange={(e) =>
                    setSelectedMap((prev) =>
                      prev
                        ? {
                            ...prev,
                            featureFlags: {
                              ...prev.featureFlags,
                              enablePlaylists: e.target.checked,
                            },
                          }
                        : null,
                    )
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Enable Playlists</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedMap?.featureFlags?.enableCrosshairs || false}
                  onChange={(e) =>
                    setSelectedMap((prev) =>
                      prev
                        ? {
                            ...prev,
                            featureFlags: {
                              ...prev.featureFlags,
                              enableCrosshairs: e.target.checked,
                            },
                          }
                        : null,
                    )
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Enable Crosshairs</span>
              </label>
            </div>
          </div>

          {/* Map Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Map Type</label>
            <select
              value={selectedMap?.mapType || 'projects'}
              onChange={(e) =>
                setSelectedMap((prev) =>
                  prev ? { ...prev, mapType: e.target.value as 'projects' | 'operations' } : null,
                )
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="projects">Projects</option>
              <option value="operations">Operations</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button color="zinc" onClick={() => setIsEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </div>
      </Dialog>
    </div>
  );
}
